import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Rocket } from "lucide-react";
import { useSuspenseGetSubscription } from "@/app/billing/api/hooks";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { PricingCard } from "./components/pricing-card";
import type { Plan } from "./plans";
import { plans } from "./plans";
import { isNextPlan } from "./utils";

export function PricingComponent() {
  const { organizationId } = useOrgProjectRouteContext();
  const { data: subscription } = useSuspenseGetSubscription(organizationId);

  const currentPlanId = subscription?.plan?.external_plan_id || "enterprise";

  const getFirstUpgradePlan = (plansArray: Plan[]) =>
    plansArray.find((plan) => isNextPlan(currentPlanId, plan.id))?.id || null;

  const firstUpgrade = getFirstUpgradePlan(plans);

  return (
    <div className="mt-4 font-sans">
      <div className="mx-auto w-full px-2">
        <div className="mx-auto grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
          {plans.map((plan) => (
            <PricingCard
              key={plan.name}
              {...plan}
              isCurrent={currentPlanId === plan.id}
              isNext={plan.id === firstUpgrade}
            />
          ))}
        </div>
      </div>

      <div className="mt-6 mb-6 px-2 md:mt-12">
        <div className="w-full rounded-md border border-border bg-card px-3 py-6 md:px-8">
          <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <Rocket
                aria-hidden="true"
                className="mt-1 hidden h-7 w-7 flex-shrink-0 text-lime-400 md:block"
              />
              <div className="max-w-4xl">
                <p className="text-lime-400 text-md uppercase tracking-wider">
                  Startup Program
                </p>
                <p className="mt-1 text-muted-foreground text-sm md:text-base">
                  Startups, nonprofits, educational institutions, and other
                  small companies under 100 employees:
                  <span className="font-semibold text-lime-400 italic">
                    {" "}
                    We got you!
                  </span>{" "}
                  Reach out to us at
                  <a
                    className="ml-1 text-foreground underline underline-offset-4 transition-colors hover:text-lime-400"
                    href="mailto:contact@arcade.dev"
                  >
                    contact@arcade.dev
                  </a>
                  . We’d love to learn about what you’re building.
                </p>
              </div>
            </div>
            <a
              aria-label="Contact us about the startup program"
              className="w-full md:w-auto"
              href="mailto:contact@arcade.dev"
            >
              <Button
                className="w-full uppercase tracking-wider md:w-auto"
                variant="outline"
              >
                Contact us
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
